import { HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { OrderChangePAXRequest } from 'src/app/model/request/order-change-pax-request';
import { OrderChangeSplitRequest } from 'src/app/model/request/order-change-split-request';
import { Passenger, PassengerDetailScreen } from 'src/app/model/screen/passenger-detail-screen';
import { YourBookingScreen } from 'src/app/model/screen/your-booking-screen';
import { ApiService } from 'src/app/service/api.service';
import { CommonService } from 'src/app/service/common.service';
import { LocalAssetReaderService } from 'src/app/service/local-asset-reader.service';
import { SandboxPageStateService } from 'src/app/service/sandbox-page-state.service';
import { XmlToJsonParserService } from 'src/app/service/xml-to-json-parser.service';
import orderChangeJSON from 'src/assets/json/split-booking/request/order-change-request-default-params.json';

interface Contact {
  address1: string;
  address2: string;
  city: string;
  zip: string;
  countrycode: string;
  email: string;
  phone: Phone;
}

interface Phone {
  countryDialingCode: string;
  areaCode: string;
  phoneNumber: string;
}

@Component({
  selector: 'app-split-booking-screen',
  templateUrl: './split-booking-screen.component.html',
  styleUrls: ['./split-booking-screen.component.scss']
})
export class SplitBookingScreenComponent implements OnInit {
  inputParamsMap = new Map();

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  separateDialCode = false;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom, CountryISO.Spain, CountryISO.Germany, CountryISO.France, CountryISO.Russia];

  passengerDetailScreenData: PassengerDetailScreen = {} as PassengerDetailScreen;

  passengerDetailExistingOrder: Passenger[] = [];
  passengerDetailNewOrder: Passenger[] = [];

  contactForm: FormGroup = new FormGroup({
    address1: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(30)]),
    address2: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(30)]),
    city: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(20)]),
    zip: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(10)]),
    countrycode: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(5)]),
    email: new FormControl('', [Validators.required, Validators.email, Validators.minLength(5), Validators.maxLength(30)]),
    phone: new FormControl('', [Validators.required])
  });

  submitted = false;

  orderChangePAXRequest: OrderChangePAXRequest = {} as OrderChangePAXRequest;
  orderChangeSplitRequest: OrderChangeSplitRequest = {} as OrderChangeSplitRequest;

  constructor(
    private localAssetReaderService: LocalAssetReaderService,
    private apiService: ApiService,
    private sandboxPageStateService: SandboxPageStateService,
    private xmlToJsonParserService: XmlToJsonParserService,
    private commonService: CommonService
  ) {
    Object.entries(orderChangeJSON).forEach((entry) => {
      const [key, value] = entry;
      this.inputParamsMap.set(key, value);
    });
  }

  get countries() {
    return this.commonService.getCountries();
  }

  ngOnInit(): void {
    this.passengerDetailScreenData = this.sandboxPageStateService.getPassengerDetailScreenData();

    this.passengerDetailScreenData.passengerDetail &&
      this.passengerDetailScreenData.passengerDetail.forEach((passenger, index) => {
        if (this.passengerDetailScreenData.passengerBList!.includes(index)) {
          this.passengerDetailNewOrder.push(passenger);
        } else {
          this.passengerDetailExistingOrder.push(passenger);
        }
      });
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.contactForm.controls;
  }

  async splitBooking() {
    this.submitted = true;

    if (
      this.contactForm.controls['address1'].invalid ||
      this.contactForm.controls['address2'].invalid ||
      this.contactForm.controls['city'].invalid ||
      this.contactForm.controls['zip'].invalid ||
      this.contactForm.controls['countrycode'].invalid ||
      this.contactForm.controls['email'].invalid ||
      !this.contactForm.value.phone
    ) {
      return;
    } else {
      const contact: Contact = {
        address1: this.contactForm.value.address1,
        address2: this.contactForm.value.address2,
        city: this.contactForm.value.city,
        zip: this.contactForm.value.zip,
        countrycode: this.contactForm.value.countrycode,
        email: this.contactForm.value.email,
        phone: {
          countryDialingCode: this.contactForm.value.phone && this.contactForm.value.phone.dialCode,
          areaCode: this.contactForm.value.phone && this.contactForm.value.phone.dialCode,
          phoneNumber: this.contactForm.value.phone && this.contactForm.value.phone.e164Number
        }
      };
      // console.log('phone : ', this.contactForm.value.phone);

      // console.log('contact : ', contact);

      await this.initOrderChangePAXRequest(contact);

      await this.apiService.getXMLResponse(
        this.sandboxPageStateService.ORDER_CHANGE_URL,
        JSON.stringify(this.orderChangePAXRequest),
        this.inputParamsMap,
        {
          headers: new HttpHeaders({
            soapAction: 'OrderChange17_2_V4'
          }),
          responseType: 'text'
        },
        'OrderChange#OrderChangePAX'
      );

      this.initOrderChangeSplitRequest();
    }
  }

  async initOrderChangePAXRequest(contact: Contact) {
    let jsonRequestTemplate = await this.localAssetReaderService.getContent(this.sandboxPageStateService.ORDERCHANGE_PAX_REQUEST_TEMPLATE);
    this.orderChangePAXRequest = JSON.parse(jsonRequestTemplate as any);

    this.inputParamsMap.set('##OrderID##Owner##', 'BA');
    this.inputParamsMap.set('##OrderID##_##', this.sandboxPageStateService.getYourBookingScreenData().booking!.pnrNumber);
    this.inputParamsMap.set('##New##PassengerID1##', this.passengerDetailNewOrder[0].passengerId);
    this.inputParamsMap.set('##Individual##GivenName1##', this.passengerDetailNewOrder[0].passengerName);
    this.inputParamsMap.set('##Individual##Surname1##', this.passengerDetailNewOrder[0].passengerSurName);
    this.inputParamsMap.set('##New##PassengerID2##', this.passengerDetailExistingOrder[0].passengerId);
    this.inputParamsMap.set('##Individual##GivenName2##', this.passengerDetailExistingOrder[0].passengerName);
    this.inputParamsMap.set('##Individual##Surname2##', this.passengerDetailExistingOrder[0].passengerSurName);
    this.inputParamsMap.set('##PostalAddress##Street1##', contact.address1);
    this.inputParamsMap.set('##PostalAddress##Street2##', contact.address2);
    this.inputParamsMap.set('##PostalAddress##PostalCode##', contact.zip);
    this.inputParamsMap.set('##PostalAddress##CityName##', contact.city);
    this.inputParamsMap.set('##PostalAddress##CountryCode##', contact.countrycode);
    this.inputParamsMap.set('##EmailAddress##EmailAddressValue##', contact.email);
    this.inputParamsMap.set('##Phone##CountryDialingCode##', contact.phone.countryDialingCode && contact.phone.countryDialingCode.substring(1));
    this.inputParamsMap.set('##Phone##AreaCode##', contact.phone.areaCode && contact.phone.areaCode.substring(1));
    this.inputParamsMap.set('##Phone##PhoneNumber##', contact.phone.phoneNumber && contact.phone.phoneNumber.substring(1));
  }

  updateOrderItemIds() {
    let orderChangePAXResponseXML = this.sandboxPageStateService.getResponseFromMap(this.sandboxPageStateService.ORDER_CHANGE_URL);
    let orderChangePAXResponseJSONData = this.xmlToJsonParserService.parseXMLToJSON(orderChangePAXResponseXML);

    let orderItems: [] = orderChangePAXResponseJSONData['SOAP:ENVELOPE']['SOAP:BODY'][0]['ORDERVIEWRS'][0]['RESPONSE'][0]['ORDER'][0]['ORDERITEMS'][0]['ORDERITEM'];

    // console.log('order items : ', orderItems);

    let pnrNumber = this.sandboxPageStateService.getYourBookingScreenData().booking!.pnrNumber;

    orderItems.forEach((orderItem: { $: { ORDERITEMID: string } }) => {
      let orderItemID: string = orderItem.$.ORDERITEMID;
      // console.log('orderItemID : ', orderItemID);
      const orderItemIDParams = orderItemID.split('-');
      if (
        orderItemID.includes(pnrNumber) &&
        orderItemIDParams &&
        orderItemIDParams.length > 0 &&
        orderItemIDParams[orderItemIDParams.length - 1].includes('FS') &&
        this.hasPassengerId(orderItemIDParams[1])
      ) {
        this.orderChangeSplitRequest['S:Envelope']['S:Body'].OrderChangeRQ.Query.OrderServicing.Delete.OrderItemID.push(orderItemID);
      }
    });
  }

  hasPassengerId(orderItemIDPart: string) {
    return this.passengerDetailNewOrder.findIndex((passenger) => orderItemIDPart === passenger.passengerId) > -1;
  }

  async initOrderChangeSplitRequest() {
    let patchSandboxSourceData: any = '';
    if (this.sandboxPageStateService.getErrorMessage() !== '') {
      patchSandboxSourceData = { showError: 'Yes', errorMessage: this.sandboxPageStateService.getErrorMessage() };
      this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
    } else {
      let jsonRequestTemplate = await this.localAssetReaderService.getContent(this.sandboxPageStateService.ORDERCHANGE_SPLIT_REQUEST_TEMPLATE);
      this.orderChangeSplitRequest = JSON.parse(jsonRequestTemplate as any);

      await this.updateOrderItemIds();

      await this.apiService.getXMLResponse(
        this.sandboxPageStateService.ORDER_CHANGE_URL,
        JSON.stringify(this.orderChangeSplitRequest),
        this.inputParamsMap,
        {
          headers: new HttpHeaders({
            soapAction: 'OrderChange17_2_V4'
          }),
          responseType: 'text'
        },
        'OrderChange#OrderChangeSplit'
      );
    }

    this.updateYourBookingScreen(this.sandboxPageStateService.ORDER_CHANGE_URL);
  }

  async updateYourBookingScreen(url: any) {
    let patchSandboxSourceData: any = '';
    if (this.sandboxPageStateService.getErrorMessage() !== '') {
      patchSandboxSourceData = { showError: 'Yes', errorMessage: this.sandboxPageStateService.getErrorMessage() };
      this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
    } else {
      let yourBookingScreen: YourBookingScreen = {} as YourBookingScreen;
      yourBookingScreen.xml = this.sandboxPageStateService.getResponseFromMap(url);
      yourBookingScreen.jsonData = this.xmlToJsonParserService.parseXMLToJSON(yourBookingScreen.xml!);
      yourBookingScreen.bookingType = 'post-booking';
      yourBookingScreen.displayConfirmBookingForm = false;

      this.sandboxPageStateService.updateYourBookingScreenData(yourBookingScreen);

      let order = yourBookingScreen.jsonData['SOAP:ENVELOPE']['SOAP:BODY'][0]['ORDERVIEWRS'][0]['RESPONSE'][0]['ORDER'][0];

      let pnrNumbers: string[] = [];

      order.BOOKINGREFERENCES[0].BOOKINGREFERENCE.forEach((bookingReference: { ID: string }) => {
        pnrNumbers.push(bookingReference.ID && bookingReference.ID[0]);
      });

      // console.log('order : ', order);

      if (this.sandboxPageStateService.getWarningMessage() !== '') {
        patchSandboxSourceData = { showWarning: 'Yes', warningMessage: this.sandboxPageStateService.getWarningMessage() };
      }

      patchSandboxSourceData = {
        ...patchSandboxSourceData,
        findOrderFormValue: order.$.ORDERID,
        bookingReferences: { pnrNumbers: pnrNumbers },
        mainMenu: 'your-booking-screen',
        sandboxScreen: 'your-booking-screen'
      };

      this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
    }
  }

  splitBookingClose() {
    let patchSandboxSourceData = { mainMenu: 'your-booking-screen' };
    this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
  }
}
